import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import { authService, AuthUser } from '../services/authService';
import { configurationService } from '../services/configurationService';

export const ProfilePage: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [platformAdminEmail, setPlatformAdminEmail] = useState<string>('support@goviq.com');

  useEffect(() => {
    // Fetch DB-sourced role so profile page shows actual role
    authService.getUserAsync().then(currentUser => {
      setUser(currentUser);

      // Fetch platform admin email for TenantAdmin users
      if (currentUser?.role === 'TenantAdmin') {
        configurationService.getPlatformAdminEmail()
          .then(email => setPlatformAdminEmail(email))
          .catch(() => setPlatformAdminEmail('support@goviq.com'));
      }
    });
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await authService.logout();
      // The logout will redirect, so we don't need to do anything else
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };

  if (!user) {
    return (
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          Profile
        </Typography>
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="body1">
            Loading user profile...
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Profile
      </Typography>
      
      <Paper sx={{ p: 3, mt: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar sx={{ width: 64, height: 64, mr: 2, bgcolor: 'primary.main' }}>
            <PersonIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h5" component="h2">
              {user.displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Account Information
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>User ID:</strong> {user.userId}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Email:</strong> {user.email}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Role:</strong> {user.role}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Last Login:</strong> {new Date(user.lastLoginDate).toLocaleString()}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Status:</strong> {user.isActive ? 'Active' : 'Inactive'}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {user.role === 'TenantAdmin' && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Need Help?
              </Typography>
              <Paper 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  bgcolor: 'background.default',
                  borderColor: 'primary.main',
                  borderWidth: 1
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <EmailIcon sx={{ mr: 1, mt: 0.5, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Account Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      To change your plan, cancel service, or for any other account inquiries, 
                      please contact our support team.
                    </Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<EmailIcon />}
                  href={`mailto:${platformAdminEmail}?subject=Inquiry from Tenant ${user.userId}&body=Please provide us with details regarding your inquiry:%0D%0A%0D%0A`}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Contact Us
                </Button>
              </Paper>
            </Box>

            <Divider sx={{ my: 2 }} />
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            {isLoggingOut ? 'Signing Out...' : 'Sign Out'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};